import { Box, Button, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import SingleDatePicker from "../../../components/form/SingleDatePicker";
import React, { useEffect, useRef, useState } from "react";
import ProfileCard from "../../common/ProfileCard";
import ItemDetailsTable from "../../common/ItemDetailsTable";
import pricingEngineForDocuments from "../../../utils/pricingEngineForDocuments";
import AddLineItemButton from "../../../components/form/AddLineItemButton";
import BusinessProfileSelect from "../../../components/form/BusinessProfileSelect";
import CustomerSelect from "../../../components/form/CustomerSelect";
import dayjs from "dayjs";
import TopBar from "../../../components/TopBar";
import { map } from "lodash";
import { toReactHookFormErrorPath } from "../../../utils/apiErrorPaths";
import {
  toApiAdditionalItemIdentification,
  toFormAdditionalItemIdentification,
} from "../../../utils/additionalItemIdentification";

import DocumentCurrencyInput from "../../../components/form/DocumentCurrencyInput";
import CustomTextInput from "../../../components/CustomTextInput";
import DocumentSummarySection from "../../../components/DocumentSummarySection";
import CopyTextComponent from "../../../components/CopyTextComponent";
import { useSelector } from "react-redux";
import DocumentLevelChargeAndAllowancesButton from "../../common/DocumentLevelChargeAndAllowancesButton";
import DocumentDetailedPeppolStatus from "../../common/DocumentDetailedPeppolStatus";
import DocumentTimeline from "../../common/DocumentTimeline";
import {
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import {
  CREDIT_NOTE_DOCUMENT_TYPE_CODE,
  DEFAULT_CURRENCY_CODE,
  DEFAULT_DOCUMENT_SOURCE,
  DEFAULT_PURCHASE_TRANSACTION_TYPE_CODE,
  DEFAULT_SALES_TRANSACTION_TYPE_CODE,
  OMN_COUNTRY_CODE,
  PURCHASE_CREDIT_NOTE_DOCUMENT_TYPE_CODE,
} from "../../../constants/defaults";
import PaymentMeansFields from "../../../components/form/PaymentMeansFields";
import TransactionTypeFields from "../../../components/form/TransactionTypeFields";
import { useNavigate } from "react-router-dom";
import { CreditNotesValidation } from "../../../validations/CreditNotesValidation";
import { yupResolver } from "@hookform/resolvers/yup";
import DocumentReasonCodeSelect from "../../common/DocumentReasonCodeSelect";
import AddPrepaymentDetailsButton from "../../common/AddPrepaymentDetailsButton";
import SupplierProfileSelect from "../../../components/form/SupplierProfileSelect";
import { isPrepaymentTransactionType } from "../../common/isPrepaymentTransactionType";
import {
  getCountryOptionByCode,
  getSubdivisionOptionByCode,
} from "../../../api/codelist/codeListsSlice";
import { transactionTypePositionCheck } from "../../../utils/helper";
import DocumentNoteField from "../../common/DocumentNoteField";
import {toApiAdditionalItemProperties, toFormAdditionalItemProperties} from "../../../utils/additionalItemProperty";
import {useAutoPopulateForm} from "../../../hooks/useAutoPopulateForm";

export default function CreditNoteForm({
  initialData = {},
  onSubmit,
  onCancel,
  readOnly = false,
  apiErrors = {},
  isLoading = false,
  isPurchase = false,
  isResubmit = false,
  isLinkedFlow = false,
}) {
  const [__, setChargeEditing] = useState(false);
  const [_, setAllowanceEditing] = useState(false);
  const { settings } = useSelector((state) => state.org.org);
  const billingRef = initialData?.billing_reference?.[0] ?? null;
  const linkedInvoiceReferenceDefaultsRef = useRef({
    invoice_reference_id: billingRef?.id,
    invoice_reference_date: billingRef?.issue_date
      ? dayjs(billingRef?.issue_date)
      : null,
    invoice_reference_uuid: billingRef?.uuid,
  });
  const [inputValue, setInputValue] = useState("");
  const isFieldLocked = readOnly || isLinkedFlow;
  const [profilesData, setProfilesData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [supplierData, setSupplierData] = useState(null);
  const navigate = useNavigate();
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);

  const DUMMY_UUID = "00000000-0000-0000-0000-000000000000";

  const backAction = () => {
    if (readOnly) {
      navigate(-1);
    } else {
      onCancel();
    }
  };

  const handleTimelineOpen = () => setIsTimelineOpen(true);
  const handleTimelineClose = () => setIsTimelineOpen(false);

  const countryOption = useSelector(
    getCountryOptionByCode(
      initialData.delivery?.delivery_location?.address?.country_code,
    ),
  );
  const subdivisionOption = useSelector(
    getSubdivisionOptionByCode(
      initialData.delivery?.delivery_location?.address?.country_subentity,
    ),
  );

  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    setError,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: yupResolver(CreditNotesValidation),
    mode: "onChange",
    defaultValues: {
      credit_note_type_code: isPurchase
        ? PURCHASE_CREDIT_NOTE_DOCUMENT_TYPE_CODE
        : CREDIT_NOTE_DOCUMENT_TYPE_CODE,
      transaction_type:
        initialData?.transaction_type || isPurchase
          ? DEFAULT_PURCHASE_TRANSACTION_TYPE_CODE
          : DEFAULT_SALES_TRANSACTION_TYPE_CODE,
      document_source: DEFAULT_DOCUMENT_SOURCE,

      discrepancy_response: initialData?.discrepancy_response || "",
      document_number: initialData?.document_number || "",
      issue_date: dayjs(initialData?.issue_date) || null,

      invoice_reference_id: billingRef?.id ?? "",
      invoice_reference_date: billingRef?.issue_date
        ? dayjs(billingRef?.issue_date)
        : null,
      // Dummy UUID for standalone credit note
      invoice_reference_uuid:
        (billingRef?.uuid ?? !isFieldLocked) ? DUMMY_UUID : "",

      reason_code: initialData?.reason_code || "",
      payment_means: initialData?.payment_means || [],
      document_lines: (initialData?.document_lines ?? []).map(
        (item, index) => ({
          ...item,
          additional_item_identification: toFormAdditionalItemIdentification(
            item?.additional_item_identification,
          ),
          additional_item_property: toFormAdditionalItemProperties(
              item?.additional_item_property
          ),
          key: item?.key || `ITM-${index}`,
        }),
      ),

      document_currency_code: initialData?.document_currency_code || "OMR",
      tax_exchange_rate: initialData?.tax_exchange_rate || null,
      accounting_supplier_party: initialData?.accounting_supplier_party || null,
      accounting_customer_party: initialData?.accounting_customer_party || null,
      charges: initialData?.charges || [],
      allowances: initialData?.allowances || [],

      invoice_period_start_date: initialData?.invoice_period?.start_date
        ? dayjs(initialData.invoice_period.start_date)
        : null,
      invoice_period_end_date: initialData?.invoice_period?.end_date
        ? dayjs(initialData.invoice_period.end_date)
        : null,
      invoice_period_description:
        initialData?.invoice_period?.description || null,
      delivery_address_line:
        initialData?.delivery?.delivery_location?.address?.address_line || null,
      delivery_additional_street_name:
        initialData?.delivery?.delivery_location?.address,
      delivery_party_name: initialData?.delivery?.party_name || null,
      delivery_incoterms: initialData?.delivery?.terms || null,
      delivery_party_id: initialData?.delivery?.party_id || null,
      delivery_date: initialData?.delivery?.actual_delivery_date
        ? dayjs(initialData.delivery?.actual_delivery_date)
        : null,
      delivery_street_name:
        initialData?.delivery?.delivery_location?.address?.street_name || null,
      delivery_city_name:
        initialData?.delivery?.delivery_location?.address?.city_name || null,
      delivery_country_code: initialData?.delivery?.delivery_location?.address
        ?.country_code
        ? countryOption
        : null,
      delivery_country_subentity:
        countryOption?.code === OMN_COUNTRY_CODE
          ? subdivisionOption
          : initialData.delivery?.delivery_location?.address?.country_subentity,

      delivery_postal_zone:
        initialData?.delivery?.delivery_location?.address?.postal_zone || null,
      prepaid_amount: initialData?.prepaid_amount || null,
      prepayment_invoice_reference:
        initialData?.prepayment_invoice_reference || null,
      note: initialData?.note || "",
    },
  });

  const addPrepaidAmount = (prepaidAmountValue) => {
    setValue("prepaid_amount", prepaidAmountValue, { shouldValidate: true });
  };

  const addPrepaymentDetails = (details) => {
    setValue("prepayment_invoice_reference", details, { shouldValidate: true });
  };

  useEffect(() => {
    map(apiErrors, (value, key) => {
      setError(toReactHookFormErrorPath(key), {
        message: value,
        type: "string",
      });
    });
  }, [setError, apiErrors]);

  const {
    fields: lineItems,
    replace: replaceLineItems,
    append: appendLineItem,
    remove: removeLineItem,
  } = useFieldArray({
    control,
    name: "document_lines",
  });
  const prepaidAmount = useWatch({
    control,
    name: "prepaid_amount",
    defaultValue: initialData?.prepaid_amount ?? null,
  });

  const { remove: chargeRemove } = useFieldArray({
    control: control,
    name: "charges",
  });

  const { remove: allowanceRemove } = useFieldArray({
    control: control,
    name: "allowances",
  });

  const handleSetBilledTo = (data) => {
    setValue("accounting_customer_party", data);
  };

  useAutoPopulateForm({
    profiles: profilesData,
    customers: customerData,
    suppliers: supplierData,
    documentType: isPurchase ? "PURCHASE" : "SALES",
    setValue,
    getValues,
  });

  const billedBy = useWatch({ control, name: "accounting_supplier_party" });
  const billedTo = useWatch({ control, name: "accounting_customer_party" });
  const document_lines = useWatch({ control, name: "document_lines" }) || [];
  const docCharges = useWatch({ control, name: "charges" }) || [];
  const docAllowances = useWatch({ control, name: "allowances" }) || [];
  const watchedPaymentMeans = useWatch({ control, name: "payment_means" });

  const conversionRate = useWatch({
    control,
    name: "tax_exchange_rate.calculation_rate",
  });

  const documentDate = useWatch({ control, name: "issue_date" });
  const profileExecutionId = useWatch({ control, name: "transaction_type" });
  const creditNoteTypeCode = useWatch({
    control,
    name: "credit_note_type_code",
  });
  const previousCreditNoteTypeCodeRef = useRef(creditNoteTypeCode);
  const isPrepayment = isPrepaymentTransactionType(profileExecutionId);
  const wasPrepayment = useRef(isPrepayment);

  useEffect(() => {
    if (readOnly) {
      return;
    }

    if (isLinkedFlow) {
      const defaults = linkedInvoiceReferenceDefaultsRef.current;
      setValue("invoice_reference_id", defaults.invoice_reference_id, {
        shouldValidate: true,
      });
      setValue("invoice_reference_date", defaults.invoice_reference_date, {
        shouldValidate: true,
      });
      setValue("invoice_reference_uuid", defaults.invoice_reference_uuid, {
        shouldValidate: true,
      });
    }
  }, [readOnly, isLinkedFlow, setValue]);
  useEffect(() => {
    const previousCreditNoteTypeCode = previousCreditNoteTypeCodeRef.current;
    if (
      !previousCreditNoteTypeCode ||
      previousCreditNoteTypeCode === creditNoteTypeCode
    ) {
      previousCreditNoteTypeCodeRef.current = creditNoteTypeCode;
      return;
    }

    setValue("accounting_supplier_party", null, { shouldValidate: true });
    setValue("profile_execution_id", DEFAULT_SALES_TRANSACTION_TYPE_CODE, {
      shouldValidate: true,
    });

    const resetDocumentLines = (getValues("document_lines") || []).map(
      (line) => ({
        ...line,
        classified_tax_category: {
          id: null,
          percent: 0,
          tax_exemption_reason_code: null,
          tax_exemption_reason: null,
          tax_scheme: line?.classified_tax_category?.tax_scheme || "VAT",
        },
      }),
    );
    setValue("document_lines", resetDocumentLines, { shouldValidate: true });

    const resetDocLevelTaxCategory = (records) =>
      (records || []).map((record) => ({
        ...record,
        tax_category: {
          id: null,
          percent: 0,
          tax_exemption_reason_code: null,
          tax_exemption_reason: null,
          tax_scheme: record?.tax_category?.tax_scheme || "VAT",
        },
      }));

    setValue("allowances", resetDocLevelTaxCategory(getValues("allowances")), {
      shouldValidate: true,
    });
    setValue("charges", resetDocLevelTaxCategory(getValues("charges")), {
      shouldValidate: true,
    });
    trigger([
      "accounting_supplier_party",
      "profile_execution_id",
      "document_lines",
      "allowances",
      "charges",
    ]);

    previousCreditNoteTypeCodeRef.current = creditNoteTypeCode;
  }, [creditNoteTypeCode, getValues, setValue, trigger]);

  useEffect(() => {
    // Re-trigger due_date validation when document_date changes
    trigger("due_date");
  }, [documentDate, trigger]);

  useEffect(() => {
    if (!readOnly && wasPrepayment.current && !isPrepayment) {
      setValue("prepaid_amount", null);
      setValue("prepayment_invoice_reference", null);
    }
    wasPrepayment.current = isPrepayment;
  }, [isPrepayment, readOnly, setValue]);

  const handleFormSubmit = async (data) => {
    // Transform document_lines to ensure proper price structure
    const transformedDocumentLines = data.document_lines?.map((line) => ({
      ...line,
      additional_item_identification: toApiAdditionalItemIdentification(
        line.additional_item_identification,
      ),
      additional_item_property: toApiAdditionalItemProperties(
          line.additional_item_property,
      ),
      price: {
        base_amount:
          line.price?.base_amount || line.price?.allowance?.base_amount || 1,
        base_quantity: line.price?.base_quantity || 1,
        allowance: {
          amount: line.price?.allowance?.amount || 0,
          base_amount:
            line.price?.base_amount || line.price?.allowance?.base_amount || 1,
          reason_code: line.price?.allowance?.reason_code,
          reason: line.price?.allowance?.reason,
          tax_category: line.price?.allowance?.tax_category,
        },
      },
    }));

    const {
      invoice_period_start_date,
      invoice_period_end_date,
      invoice_period_description,
      ...restData
    } = data;

    const hasInvoicePeriod =
      invoice_period_start_date ||
      invoice_period_end_date ||
      invoice_period_description;

    const shouldSendDeliveryData =
      transactionTypePositionCheck(data.transaction_type, 7) ||
      transactionTypePositionCheck(data.transaction_type, 13) ||
      transactionTypePositionCheck(data.transaction_type, 12);

    const payload = {
      ...restData,
      accounting_supplier_party: {
        ...data.accounting_supplier_party,
        uuid: data.accounting_supplier_party.id,
      },
      accounting_customer_party: {
        ...data.accounting_customer_party,
        uuid: data.accounting_customer_party.id,
      },
      document_lines: transformedDocumentLines,
      due_date: dayjs(data.due_date).format("YYYY-MM-DD"),
      issue_date: dayjs(data.issue_date).format("YYYY-MM-DD"),
      issue_time: dayjs().format("HH:mm:ss"),
      invoice_period: hasInvoicePeriod
        ? {
            start_date: invoice_period_start_date
              ? dayjs(invoice_period_start_date).format("YYYY-MM-DD")
              : null,
            end_date: invoice_period_end_date
              ? dayjs(invoice_period_end_date).format("YYYY-MM-DD")
              : null,
            description: invoice_period_description || null,
          }
        : null,
      billing_reference: [
        {
          id: data.invoice_reference_id,
          issue_date: dayjs(data.invoice_reference_date).format("YYYY-MM-DD"),
          uuid: data.invoice_reference_uuid,
        },
      ],
      sub_total: subtotal,
      total_excluding_tax: totalExcludingTax,
      total_tax: totalTaxAmount,
      total_including_tax: totalIncludingTax,
      total_payable: totalPayableAmount,
      header_allowance: headerAllowance,
      header_charges: headerCharges,
      ...(shouldSendDeliveryData && {
        delivery: {
          party_name: data?.delivery_party_name,
          actual_delivery_date: data?.delivery_date
            ? dayjs(data.delivery_date).format("YYYY-MM-DD")
            : null,
          terms: data?.delivery_incoterms,
          party_id: data?.delivery_party_id,
          delivery_location: {
            address: {
              additional_street_name: data?.delivery_additional_street_name,
              address_line: data?.delivery_address_line,
              street_name: data?.delivery_street_name,
              city_name: data?.delivery_city_name,
              country_code: data?.delivery_country_code?.code,
              country: data?.delivery_country_code?.label,
              country_subentity:
                data?.delivery_country_code?.code === OMN_COUNTRY_CODE
                  ? data?.delivery_country_subentity?.code
                  : data?.delivery_country_subentity,
              postal_zone: data?.delivery_postal_zone,
            },
          },
        },
      }),
      total_paid: 0,
      ...(data?.document_currency_code !== DEFAULT_CURRENCY_CODE && {
        tax_currency_code: DEFAULT_CURRENCY_CODE,
      }),
    };
    onSubmit(payload);
  };

  const {
    itemsSubtotal,
    subtotal,
    totalTaxAmount,
    taxInOMN,
    totalItemAllowances,
    totalExcludingTax,
    totalIncludingTax,
    totalItemCharges,
    totalPayableAmount,
    totalTaxableAmount,
    totalNonTaxableAmount,
    taxBreakdown,
    headerCharges,
    headerAllowance,
    payableRoundingAmount,
  } = pricingEngineForDocuments({
    items: document_lines,
    docCharges: docCharges,
    docAllowances: docAllowances,
    conversionRatePerOMN: conversionRate,
    prepaidAmount: prepaidAmount,
    ...(readOnly &&
      initialData?.payable_rounding_amount != null && {
        payableRoundingAmount: initialData.payable_rounding_amount,
      }),
  });

  const getAllErrorMessages = (error) => {
    if (!error || typeof error !== "object") return [];

    return Object.entries(error).flatMap(([key, value]) => {
      if (key === "message" && typeof value === "string") {
        return [value];
      }

      return getAllErrorMessages(value);
    });
  };

  const getSellersErrorMessage = (error) => {
    if (!error?.accounting_supplier_party) return null;

    const messages = getAllErrorMessages(error.accounting_supplier_party);

    return messages.length ? messages.join(", ") : null;
  };

  const getBuyersErrorMessage = (error) => {
    if (!error?.accounting_customer_party) return null;

    const messages = getAllErrorMessages(error.accounting_customer_party);

    return messages.length ? messages.join(", ") : null;
  };

  const sellersErrorMessage = getSellersErrorMessage(errors);
  const buyersErrorMessage = getBuyersErrorMessage(errors);

  return (
    <>
      <Box className="InvoiceDetails" sx={{ pt: 2, pb: 4 }}>
        <Grid
          container
          sx={{
            display: "block",
          }}
        >
          <Grid>
            <TopBar
              title={
                <Stack direction="row" gap={3}>
                  {readOnly ? (
                    <>
                      {"Credit Note Details |"}{" "}
                      <CopyTextComponent copyText={initialData.id} />{" "}
                    </>
                  ) : isPurchase ? (
                    "Self Billed Credit Note Form"
                  ) : (
                    "Credit Note Form"
                  )}
                </Stack>
              }
            >
              <Stack direction="row" spacing={1}>
                {readOnly && (
                  <>
                    <Button
                      data-testid="refresh-button"
                      variant="outlined"
                      color="primary"
                      onClick={() => window.location.reload()}
                    >
                      <RefreshIcon
                        fontSize="small"
                        color="primary"
                        sx={{ mr: 1 }}
                      />
                      Refresh
                    </Button>
                    {initialData && (
                      <Button
                        data-testid="timeline-button"
                        variant="outlined"
                        color="primary"
                        onClick={handleTimelineOpen}
                      >
                        <TimelineIcon
                          fontSize="small"
                          color="primary"
                          sx={{ mr: 1 }}
                        />
                        Timeline
                      </Button>
                    )}
                  </>
                )}
                <Button
                  data-testid="cancel-button"
                  variant={readOnly ? "contained" : "outlined"}
                  color="primary"
                  onClick={backAction}
                  disabled={isLoading}
                >
                  {readOnly ? "Back" : "Cancel"}
                </Button>

                {!readOnly && (
                  <Button
                    data-testid="submit-button"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit(handleFormSubmit)}
                    disabled={isLoading}
                  >
                    {"Submit"}
                  </Button>
                )}
              </Stack>
            </TopBar>
          </Grid>
        </Grid>
      </Box>

      {readOnly && initialData?.id && (
        <DocumentDetailedPeppolStatus
          documentType="credit-notes"
          docId={initialData.id}
          metaInfo={initialData?.meta_info}
          isPurchase={isPurchase}
          buyer={initialData?.accounting_customer_party}
          documentSource={initialData?.document_source}
        />
      )}

      {readOnly && initialData?.id && (
        <DocumentTimeline
          documentType="credit-notes"
          docId={initialData.id}
          isPurchase={isPurchase}
          isOpen={isTimelineOpen}
          onClose={handleTimelineClose}
        />
      )}

      <Box className="CreditNoteDetails">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <CustomTextInput
              data-testid="document-number-input"
              label="Credit Note Number"
              disabled={settings?.auto_numbering_enabled}
              helperText={
                settings?.auto_numbering_enabled
                  ? "Document number will be generated automatically"
                  : ""
              }
              control={control}
              errors={errors}
              name={"document_number"}
              readOnly={readOnly || isResubmit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <SingleDatePicker
              data-testid="credit-note-date-input"
              name="issue_date"
              label="Credit Note Date"
              control={control}
              errors={errors}
              readOnly={isFieldLocked}
              maxDate={dayjs()}
            />
          </Grid>{" "}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <CustomTextInput
              data-testid="document-number-input"
              label="Invoice Reference Id"
              control={control}
              errors={errors}
              name={"invoice_reference_id"}
              readOnly={isFieldLocked}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <SingleDatePicker
              data-testid="credit-note-due-date-input"
              name="invoice_reference_date"
              label="Invoice Issue Date"
              control={control}
              errors={errors}
              readOnly={isFieldLocked}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 8 }}>
            <DocumentReasonCodeSelect
              data-testid="credit-reason-select"
              name={`discrepancy_response`}
              control={control}
              errors={errors}
              readOnly={readOnly}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={2}>
          <PaymentMeansFields
            control={control}
            errors={errors}
            readOnly={isFieldLocked}
            watchedPaymentMeans={watchedPaymentMeans}
            setValue={setValue}
            disabled={true}
          />
          <TransactionTypeFields
            control={control}
            errors={errors}
            readOnly={isFieldLocked}
            profileExecutionId={profileExecutionId}
            setValue={setValue}
          />
        </Grid>

        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            {isPurchase ? (
              <SupplierProfileSelect
                data-testid="billed-by-select"
                label="Billed by"
                name="accounting_supplier_party"
                control={control}
                errors={sellersErrorMessage}
                readOnly={isFieldLocked}
              />
            ) : (
              <BusinessProfileSelect
                data-testid="billed-by-select"
                label="Billed by"
                name="accounting_supplier_party"
                control={control}
                errors={sellersErrorMessage}
                readOnly={isFieldLocked}
              />
            )}
            {billedBy && (
              <Box sx={{ mt: 2, fontSize: "0.875rem", color: "black" }}>
                <ProfileCard
                  title="Billed By"
                  subtitle="Seller Info"
                  document={{
                    ...billedBy,
                  }}
                />
              </Box>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            {isPurchase ? (
              <BusinessProfileSelect
                data-testid="billed-to-select"
                label="Billed to"
                name="accounting_customer_party"
                control={control}
                errors={buyersErrorMessage}
                readOnly={isFieldLocked}
              />
            ) : (
              <CustomerSelect
                data-testid="billed-to-select"
                label="Billed to"
                name="accounting_customer_party"
                control={control}
                errors={buyersErrorMessage}
                readOnly={isFieldLocked}
                onSelect={handleSetBilledTo}
                setError={setError}
              />
            )}
            {billedTo && (
              <Box sx={{ mt: 2, fontSize: "0.875rem", color: "black" }}>
                <ProfileCard
                  title="Billed To"
                  subtitle="Buyer Info"
                  rawData={billedTo}
                  readOnly={isFieldLocked}
                  onUpdate={handleSetBilledTo}
                  document={{
                    ...billedTo, // Use the selected value from the form state
                  }}
                />
              </Box>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Grid
              container
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <Grid>
                {!readOnly && (
                  <AddLineItemButton
                    errors={errors}
                    addItem={appendLineItem}
                    lineItems={lineItems}
                    transactionType={profileExecutionId}
                  />
                )}
              </Grid>
              <Grid
                container
                spacing={2}
                direction="row"
                alignItems="center"
                marginBottom={"1.2em"}
              >
                <DocumentCurrencyInput
                  control={control}
                  errors={errors}
                  documentCurrencyCode={useWatch({
                    control,
                    name: "document_currency_code",
                  })}
                  readOnly={true}
                />
              </Grid>
            </Grid>
            <ItemDetailsTable
              items={lineItems}
              remove={removeLineItem}
              replace={replaceLineItems}
              add={appendLineItem}
              readOnly={readOnly}
              transactionType={profileExecutionId}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ md: 6 }}>
            <Grid container spacing={1}>
              <Grid item md={12}>
                <DocumentLevelChargeAndAllowancesButton
                  control={control}
                  readOnly={readOnly}
                  currentNetAmount={totalExcludingTax}
                  itemBaseAmount={totalExcludingTax}
                />
              </Grid>
              <Grid size={{ md: 12 }}>
                {isPrepayment && (
                  <AddPrepaymentDetailsButton
                    control={control}
                    error={errors}
                    readOnly={isFieldLocked}
                    currentNetAmount={totalIncludingTax}
                    addPrepaidAmount={addPrepaidAmount}
                    addPrepaymentDetails={addPrepaymentDetails}
                    getValues={getValues}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ md: 6 }}>
            <DocumentSummarySection
              itemsSubtotal={itemsSubtotal}
              subtotal={subtotal}
              totalItemAllowances={totalItemAllowances}
              totalItemCharges={totalItemCharges}
              totalExcludingTax={totalExcludingTax}
              totalIncludingTax={totalIncludingTax}
              totalTaxAmount={totalTaxAmount}
              taxInOMN={taxInOMN}
              totalPayableAmount={totalPayableAmount}
              documentCurrencyCode={getValues("document_currency_code")}
              headerCharges={headerCharges}
              headerAllowance={headerAllowance}
              totalTaxableAmount={totalTaxableAmount}
              totalNonTaxableAmount={totalNonTaxableAmount}
              taxBreakdown={taxBreakdown}
              prepaidAmount={prepaidAmount}
              addPrepaidAmount={addPrepaidAmount}
              addPrepaymentDetails={addPrepaymentDetails}
              payableRoundingAmount={payableRoundingAmount}
              readOnly={readOnly}
              getValues={getValues}
            />
          </Grid>
        </Grid>
        <DocumentNoteField
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      </Box>
    </>
  );
}
